import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyWorldbankMode({ brand, today, Copy }: ModeRendererProps) {
  // World Bank gradient: development indicators
  const wbGradient = (
    <defs>
      <linearGradient id="wbGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#009FDA" />
        <stop offset="50%" stopColor="#00AB51" />
        <stop offset="100%" stopColor="#F7941D" />
      </linearGradient>
      <radialGradient id="dataGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#00AB51" stopOpacity={0.5} />
        <stop offset="100%" stopColor="#009FDA" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Inequality heatmaps: Gini coefficient visualization
  const inequalityHeatmaps: JSX.Element[] = [];
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 12; col++) {
      const x = 80 + col * 55;
      const y = 120 + row * 55;
      const inequality = (row / 15) * 0.6 + Math.sin(col * 0.5) * 0.2 + 0.2;
      const color = inequality < 0.35 ? "#00AB51" : inequality < 0.65 ? "#F7941D" : "#E53935";
      inequalityHeatmaps.push(
        <rect
          key={`ineq-${row}-${col}`}
          x={x - 18}
          y={y - 18}
          width={36}
          height={36}
          fill={color}
          opacity={0.3 + inequality * 0.3}
          rx={4}
        />
      );
    }
  }

  // Mobility flows: economic migration patterns
  const mobilityFlows: JSX.Element[] = [];
  const flowCount = 24;
  for (let i = 0; i < flowCount; i++) {
    const angle1 = (i * Math.PI * 2) / flowCount;
    const angle2 = ((i + 8) * Math.PI * 2) / flowCount;
    const radius = 180;
    const x1 = 397 + radius * Math.cos(angle1);
    const y1 = 561.5 + radius * Math.sin(angle1);
    const x2 = 397 + radius * Math.cos(angle2);
    const y2 = 561.5 + radius * Math.sin(angle2);
    
    const cx = (x1 + x2) / 2;
    const cy = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const curveX = cx - dy * 0.2;
    const curveY = cy + dx * 0.2;
    
    mobilityFlows.push(
      <path
        key={`flow-${i}`}
        d={`M ${x1},${y1} Q ${curveX},${curveY} ${x2},${y2}`}
        fill="none"
        stroke="url(#wbGrad)"
        strokeWidth={1}
        opacity={0.3}
      />
    );
  }

  // Credit nets: financial access network
  const creditNets: JSX.Element[] = [];
  for (let i = 0; i < 14; i++) {
    const radius = 80 + i * 20;
    const nodes = 8 + i;
    for (let j = 0; j < nodes; j++) {
      const angle = (j * Math.PI * 2) / nodes;
      const x = 397 + radius * Math.cos(angle);
      const y = 561.5 + radius * Math.sin(angle);
      creditNets.push(
        <circle
          key={`credit-${i}-${j}`}
          cx={x}
          cy={y}
          r={4}
          fill="url(#wbGrad)"
          opacity={0.5}
        />
      );
    }
  }

  // Development indicator rings
  const developmentRings: JSX.Element[] = [];
  const indicators = [
    { radius: 120, label: "GDP" },
    { radius: 160, label: "HDI" },
    { radius: 200, label: "GNI" },
    { radius: 240, label: "SDG" }
  ];
  
  indicators.forEach((ind, i) => {
    developmentRings.push(
      <circle
        key={`ring-${i}`}
        cx={397}
        cy={561.5}
        r={ind.radius}
        fill="none"
        stroke="url(#wbGrad)"
        strokeWidth={2}
        opacity={0.4}
        strokeDasharray="8,4"
      />
    );
  });

  // Central World Bank core
  const worldBankCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={100} fill="url(#dataGlow)" />
      <circle cx={397} cy={561.5} r={70} fill="none" stroke="#00AB51" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={50} fill="none" stroke="#009FDA" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={30} fill="none" stroke="#F7941D" strokeWidth={2} />
      <text
        x={397}
        y={570}
        fontFamily="monospace"
        fontSize={22}
        fontWeight="bold"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        WB
      </text>
    </g>
  );

  return (
    <g>
      {wbGradient}
      {inequalityHeatmaps}
      {creditNets}
      {developmentRings}
      {mobilityFlows}
      {worldBankCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">{Copy?.title}</text>
        <text x={0} y={22} fontSize={14}>{Copy?.subtitle}</text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>{Copy?.date}</text>
      </g>
    </g>
  );
}
